import { float, mix, texture, vec2, vec4 } from 'three/tsl';
import { sampleCurve, sampleGradient } from './tsl/lib.js';
import { resolveSpriteTexture } from './sprites.js';
/** Shared registry: the compiler consumes it here; the studio editor renders palettes/handles from it. */
export const GRAPH_NODE_SPECS = {
    nAge: { label: 'Particle age (0–1)', inputs: [], outputs: [{ name: 'out', type: 'float' }] },
    seed: {
        label: 'Particle seed',
        inputs: [],
        outputs: [
            { name: 'x', type: 'float' },
            { name: 'y', type: 'float' },
            { name: 'z', type: 'float' },
            { name: 'w', type: 'float' },
        ],
    },
    uv: { label: 'UV', inputs: [], outputs: [{ name: 'out', type: 'vec2' }] },
    constFloat: {
        label: 'Float',
        inputs: [],
        outputs: [{ name: 'out', type: 'float' }],
        defaultParams: { value: 1 },
    },
    constColor: {
        label: 'Color',
        inputs: [],
        outputs: [{ name: 'out', type: 'color' }],
        defaultParams: { value: [1, 1, 1, 1] },
    },
    textureSample: {
        label: 'Texture sample',
        inputs: [{ name: 'uv', type: 'vec2' }],
        outputs: [{ name: 'out', type: 'color' }],
        defaultParams: { sprite: { type: 'builtin', id: 'softDot' } },
    },
    curveSample: {
        label: 'Curve sample',
        inputs: [{ name: 't', type: 'float' }],
        outputs: [{ name: 'out', type: 'float' }],
        defaultParams: { curve: { preset: 'linear', points: [{ t: 0, v: 0 }, { t: 1, v: 1 }] } },
    },
    gradientSample: {
        label: 'Gradient sample',
        inputs: [{ name: 't', type: 'float' }],
        outputs: [{ name: 'out', type: 'color' }],
        defaultParams: {
            gradient: { mode: 'smooth', stops: [{ t: 0, color: [1, 1, 1, 1] }, { t: 1, color: [1, 1, 1, 0] }] },
        },
    },
    uvScroll: {
        label: 'UV scroll',
        inputs: [{ name: 'uv', type: 'vec2' }],
        outputs: [{ name: 'out', type: 'vec2' }],
        defaultParams: { x: 0.5, y: 0 },
    },
    uvRotate: {
        label: 'UV rotate',
        inputs: [{ name: 'uv', type: 'vec2' }],
        outputs: [{ name: 'out', type: 'vec2' }],
        defaultParams: { degrees: 45 },
    },
    uvScale: {
        label: 'UV scale',
        inputs: [{ name: 'uv', type: 'vec2' }],
        outputs: [{ name: 'out', type: 'vec2' }],
        defaultParams: { x: 2, y: 2 },
    },
    multiply: {
        label: 'Multiply',
        inputs: [
            { name: 'a', type: 'color' },
            { name: 'b', type: 'color' },
        ],
        outputs: [{ name: 'out', type: 'color' }],
    },
    add: {
        label: 'Add',
        inputs: [
            { name: 'a', type: 'color' },
            { name: 'b', type: 'color' },
        ],
        outputs: [{ name: 'out', type: 'color' }],
    },
    mix: {
        label: 'Mix',
        inputs: [
            { name: 'a', type: 'color' },
            { name: 'b', type: 'color' },
            { name: 't', type: 'float' },
        ],
        outputs: [{ name: 'out', type: 'color' }],
    },
    toonRamp: {
        label: 'Toon ramp',
        inputs: [{ name: 'in', type: 'color' }],
        outputs: [{ name: 'out', type: 'color' }],
        defaultParams: { steps: 3 },
    },
    output: {
        label: 'Output',
        inputs: [
            { name: 'color', type: 'color' },
            { name: 'alpha', type: 'float' },
        ],
        outputs: [],
    },
};
/** Editor connection rule, mirrored by the compiler's coercion below. */
export function canConnect(from, to) {
    if (from === to)
        return true;
    if (from === 'float' && (to === 'color' || to === 'vec2'))
        return true; // float splats up
    if (from === 'color' && to === 'float')
        return true; // color narrows to its alpha channel
    return false;
}
function coerce(v, want) {
    if (v.type === want)
        return v.node;
    if (v.type === 'float' && want === 'color')
        return vec4(v.node, v.node, v.node, v.node);
    if (v.type === 'float' && want === 'vec2')
        return vec2(v.node, v.node);
    if (v.type === 'color' && want === 'float')
        return v.node.w; // alpha channel
    return null;
}
function num(params, key, fallback) {
    const v = params?.[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
export function compileGraph(graph, ctx) {
    const nodes = new Map(graph.nodes.map((n) => [n.id, n]));
    const edgeInto = new Map();
    for (const e of graph.edges)
        edgeInto.set(`${e.to}:${e.toPort}`, e);
    const memo = new Map();
    const visiting = new Set(); // cycle guard: a cycle reads as "no input"
    function input(n, port, want, fallback) {
        const e = edgeInto.get(`${n.id}:${port}`);
        if (!e)
            return fallback;
        const src = nodes.get(e.from);
        if (!src || visiting.has(src.id))
            return fallback;
        const out = evaluate(src)[e.fromPort];
        if (!out)
            return fallback;
        return coerce(out, want) ?? fallback;
    }
    function evaluate(n) {
        const cached = memo.get(n.id);
        if (cached)
            return cached;
        visiting.add(n.id);
        const p = n.params;
        let outs;
        switch (n.type) {
            case 'nAge':
                outs = { out: { type: 'float', node: ctx.nAge } };
                break;
            case 'seed':
                outs = {
                    x: { type: 'float', node: ctx.seed.x },
                    y: { type: 'float', node: ctx.seed.y },
                    z: { type: 'float', node: ctx.seed.z },
                    w: { type: 'float', node: ctx.seed.w },
                };
                break;
            case 'uv':
                outs = { out: { type: 'vec2', node: ctx.uvNode } };
                break;
            case 'constFloat':
                outs = { out: { type: 'float', node: float(num(p, 'value', 1)) } };
                break;
            case 'constColor': {
                const c = (Array.isArray(p?.value) ? p.value : [1, 1, 1, 1]);
                outs = { out: { type: 'color', node: vec4(c[0], c[1], c[2], c[3]) } };
                break;
            }
            case 'textureSample': {
                const tex = resolveSpriteTexture(p?.sprite ?? { type: 'builtin', id: 'softDot' });
                const uvIn = input(n, 'uv', 'vec2', ctx.uvNode);
                outs = { out: { type: 'color', node: tex ? texture(tex, uvIn) : vec4(1, 1, 1, 1) } };
                break;
            }
            case 'curveSample': {
                const row = ctx.graphRows[n.id];
                const t = input(n, 't', 'float', ctx.nAge);
                outs = {
                    out: {
                        type: 'float',
                        node: row !== undefined ? sampleCurve(ctx.atlas.texture, row, ctx.atlas.rows, t) : float(1),
                    },
                };
                break;
            }
            case 'gradientSample': {
                const row = ctx.graphRows[n.id];
                const t = input(n, 't', 'float', ctx.nAge);
                outs = {
                    out: {
                        type: 'color',
                        node: row !== undefined ? sampleGradient(ctx.atlas.texture, row, ctx.atlas.rows, t) : vec4(1, 1, 1, 1),
                    },
                };
                break;
            }
            case 'uvScroll': {
                const uvIn = input(n, 'uv', 'vec2', ctx.uvNode);
                const offset = vec2(num(p, 'x', 0.5), num(p, 'y', 0)).mul(ctx.time);
                outs = { out: { type: 'vec2', node: uvIn.add(offset).fract() } };
                break;
            }
            case 'uvRotate': {
                const uvIn = input(n, 'uv', 'vec2', ctx.uvNode);
                const a = (num(p, 'degrees', 45) * Math.PI) / 180;
                const cos = Math.cos(a);
                const sin = Math.sin(a);
                const centered = uvIn.sub(0.5);
                outs = {
                    out: {
                        type: 'vec2',
                        node: vec2(centered.x.mul(cos).sub(centered.y.mul(sin)), centered.x.mul(sin).add(centered.y.mul(cos))).add(0.5),
                    },
                };
                break;
            }
            case 'uvScale': {
                const uvIn = input(n, 'uv', 'vec2', ctx.uvNode);
                outs = {
                    out: {
                        type: 'vec2',
                        node: uvIn.sub(0.5).mul(vec2(num(p, 'x', 2), num(p, 'y', 2))).add(0.5).fract(),
                    },
                };
                break;
            }
            case 'multiply': {
                const a = input(n, 'a', 'color', vec4(1, 1, 1, 1));
                const b = input(n, 'b', 'color', vec4(1, 1, 1, 1));
                outs = { out: { type: 'color', node: a.mul(b) } };
                break;
            }
            case 'add': {
                const a = input(n, 'a', 'color', vec4(0, 0, 0, 0));
                const b = input(n, 'b', 'color', vec4(0, 0, 0, 0));
                outs = { out: { type: 'color', node: a.add(b) } };
                break;
            }
            case 'mix': {
                const a = input(n, 'a', 'color', vec4(0, 0, 0, 0));
                const b = input(n, 'b', 'color', vec4(1, 1, 1, 1));
                const t = input(n, 't', 'float', float(0.5));
                outs = { out: { type: 'color', node: mix(a, b, t) } };
                break;
            }
            case 'toonRamp': {
                const c = input(n, 'in', 'color', vec4(1, 1, 1, 1));
                const steps = Math.max(2, Math.round(num(p, 'steps', 3)));
                // posterize rgb, keep alpha continuous — cel banding without alpha popping
                const banded = c.xyz.mul(steps).floor().add(0.5).div(steps);
                outs = { out: { type: 'color', node: vec4(banded, c.w) } };
                break;
            }
            case 'output':
                outs = {};
                break;
        }
        visiting.delete(n.id);
        memo.set(n.id, outs);
        return outs;
    }
    const outNode = graph.nodes.find((n) => n.type === 'output');
    if (!outNode)
        return { rgb: vec4(1, 1, 1, 1).xyz, alpha: float(1) };
    const color = input(outNode, 'color', 'color', vec4(1, 1, 1, 1));
    const alpha = input(outNode, 'alpha', 'float', color.w);
    return { rgb: color.xyz, alpha };
}
/** Curve/gradient nodes need atlas rows; stable order so rebakes stay aligned. */
export function graphCurveNodes(graph) {
    return graph.nodes
        .filter((n) => n.type === 'curveSample' || n.type === 'gradientSample')
        .sort((a, b) => a.id.localeCompare(b.id));
}
